import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Drag-to-resize height for a grid.
 *
 * The report tabs sit under a header, a filter bar and a tab strip, so a fixed
 * `calc(100vh - N)` guesses wrong on short viewports — with devtools docked it
 * collapses far enough that the grid paints its header and no rows at all.
 * A clamped, user-controlled height removes the guess and the floor guarantees
 * the body is always visible.
 */
const useResizableHeight = ({ initial = 460, min = 280, max = 900 } = {}) => {
  const [height, setHeight] = useState(initial);
  const dragRef = useRef(null);

  const clamp = useCallback((value) => Math.max(min, Math.min(max, value)), [min, max]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!dragRef.current) return;
      setHeight(clamp(dragRef.current.startHeight + (event.clientY - dragRef.current.startY)));
    };
    const stopDragging = () => { dragRef.current = null; };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDragging);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopDragging);
    };
  }, [clamp]);

  const startResize = useCallback((event) => {
    event.preventDefault();
    dragRef.current = { startY: event.clientY, startHeight: height };
  }, [height]);

  const onKeyDown = useCallback((event) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    setHeight((current) => clamp(current + (event.key === 'ArrowDown' ? 40 : -40)));
  }, [clamp]);

  return { height, min, max, startResize, onKeyDown };
};

export default useResizableHeight;
